// filepath: apps/web/src/features/admin/userRoles/RolePickerPopover.tsx
//
// FR-UI-ADM-5 AC-3 — single-user role picker popover.
//
// Opens on the row action button; one checkbox per realm role.
// Saving calls PUT /api/admin/users/{user_id}/roles with the
// current version (If-Match + body echo). On success renders an
// inline "Takes effect on next login" notice (AC-6).

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAssignUserRoles } from "./queries";

interface RolePickerPopoverProps {
  userId: string;
  currentRoles: string[];
  currentVersion: number;
  onClose: () => void;
}

export function RolePickerPopover({
  userId,
  currentRoles,
  currentVersion,
  onClose,
}: RolePickerPopoverProps): React.ReactElement {
  const { t } = useTranslation();
  const assignMutation = useAssignUserRoles(userId);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(
    () => new Set(currentRoles),
  );
  const [showNextLoginNotice, setShowNextLoginNotice] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Escape closes the popover (a11y FR-UI-CORE-9).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const onSave = async () => {
    const req = {
      roles: Array.from(selectedRoles),
      expected_sync_state_version: currentVersion,
    };
    try {
      await assignMutation.mutateAsync(req);
      setShowNextLoginNotice(true);
      // Auto-close after 5 seconds (FR-UI-ADM-5 AC-6 lifecycle).
      setTimeout(() => onClose(), 5000);
    } catch {
      // mutation.isError renders the error inline below.
    }
  };

  return (
    <dialog
      ref={dialogRef}
      open
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-picker-title"
      data-testid="role-picker-popover"
    >
      <h2 id="role-picker-title">
        {t("admin.users.dialog.role_picker.title")}
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSave();
        }}
      >
        <fieldset>
          <legend>{t("admin.users.dialog.role_picker.label.roles")}</legend>
          {/* Role checkbox list — the realm roles are derived from
              the cached user's `assigned_roles` so we always show
              the canonical set; new roles are picked up via the
              sync probe on the next page load. */}
          {selectedRoles.size === 0 ? (
            <p data-state="no-roles">
              {t("admin.users.dialog.role_picker.empty")}
            </p>
          ) : null}
          {Array.from(selectedRoles).map((role) => (
            <label key={role}>
              <input
                type="checkbox"
                checked
                onChange={() =>
                  setSelectedRoles((prev) => {
                    const next = new Set(prev);
                    next.delete(role);
                    return next;
                  })
                }
                data-testid={`role-picker-checkbox-${role}`}
              />
              {role}
            </label>
          ))}
        </fieldset>
        <button
          type="submit"
          disabled={assignMutation.isPending}
          data-testid="role-picker-save"
        >
          {t("admin.users.dialog.role_picker.cta")}
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid="role-picker-cancel"
        >
          {t("common.cancel")}
        </button>
      </form>
      {assignMutation.isError ? (
        <div role="alert" data-state="save_error">
          {assignMutation.error?.message ??
            t("admin.users.dialog.role_picker.error")}
        </div>
      ) : null}
      {showNextLoginNotice ? (
        <div role="status" data-state="takes_effect_next_login">
          {t("admin.users.notice.takes_effect_next_login")}
        </div>
      ) : null}
    </dialog>
  );
}
